import { useState } from 'react';
import { Dropdown } from '@/features/common/Dropdown';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import type { EventFeedbackFormProps } from '@/features/publicsite/types/events/EventFeedbackFormProps';

/** The two textareas' ids, module constants so each <label>'s htmlFor and its control's own id
 * cannot drift apart. One event detail page renders at most one of these forms, so fixed ids
 * stay unique in the document. */
const FEEDBACK_COMMENTS_ID = 'event-feedback-comments';
const FEEDBACK_NEEDS_ID = 'event-feedback-needs';

/**
 * The post-event evaluation form (tor.md §6.11: "colectarea feedback-ului participanților,
 * evaluarea calității prezentărilor și identificarea nevoilor viitoare de instruire") — all three
 * of those, which is why the presentation rating and the training-needs field sit beside the
 * overall one rather than being folded into the comments.
 *
 * <p>Shown only once EventDetailPage has confirmed the server would accept a submission; see that
 * page's own comment on why an unregistered visitor never sees this at all.
 */
export function EventFeedbackForm({
  rating,
  comments,
  presentationRating,
  futureNeeds,
  error,
  submitting,
  onRatingChange,
  onCommentsChange,
  onPresentationRatingChange,
  onFutureNeedsChange,
  onSubmit,
}: EventFeedbackFormProps) {
  const { t } = useTranslation(publicEvents);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [presentationOpen, setPresentationOpen] = useState(false);
  return (
    <div className="event-detail__feedback">
      <h3 className="event-detail__section-heading">{t('feedbackHeading')}</h3>
      {/* role="alert", not the aria-invalid/aria-describedby pair a field validator would use:
        this text comes back from POST /feedback, so it reports the submission failing rather than
        one control holding a bad value — there is no single field to mark invalid, and it has to
        be announced when it appears rather than when something is focused. */}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <div className="event-detail__feedback-field event-detail__feedback-field--rating">
        {/* A <span>, not a <label>, for the reason FilterSelect's label spells out: Dropdown
          renders a listbox button, and a <label> neither associates with nor activates a button.
          The visible word reaches assistive tech through the control's own ariaLabel instead. */}
        <span className="event-detail__feedback-label">{t('feedbackRatingLabel')}</span>
        <Dropdown
          size="md"
          ariaLabel={t('feedbackRatingLabel')}
          placeholder={String(rating)}
          options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
          value={String(rating)}
          onChange={(v) => onRatingChange(Number(v))}
          isOpen={ratingOpen}
          onToggle={() => setRatingOpen((open) => !open)}
          onClose={() => setRatingOpen(false)}
        />
      </div>
      <div className="event-detail__feedback-field event-detail__feedback-field--rating">
        <span className="event-detail__feedback-label">{t('feedbackPresentationLabel')}</span>
        <Dropdown
          size="md"
          ariaLabel={t('feedbackPresentationLabel')}
          placeholder={String(presentationRating)}
          options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
          value={String(presentationRating)}
          onChange={(v) => onPresentationRatingChange(Number(v))}
          isOpen={presentationOpen}
          onToggle={() => setPresentationOpen((open) => !open)}
          onClose={() => setPresentationOpen(false)}
        />
      </div>
      <div className="event-detail__feedback-field">
        {/* A real <label> here, unlike the rating's: a <textarea> is a labelable control, so
          htmlFor gives it both the accessible name and the click-to-focus a <span> cannot. */}
        <label className="event-detail__feedback-label" htmlFor={FEEDBACK_COMMENTS_ID}>
          {t('feedbackCommentsLabel')}
        </label>
        <textarea
          id={FEEDBACK_COMMENTS_ID}
          className="event-detail__feedback-input"
          rows={3}
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
        />
      </div>
      <div className="event-detail__feedback-field">
        <label className="event-detail__feedback-label" htmlFor={FEEDBACK_NEEDS_ID}>
          {t('feedbackNeedsLabel')}
        </label>
        <textarea
          id={FEEDBACK_NEEDS_ID}
          className="event-detail__feedback-input"
          rows={3}
          value={futureNeeds}
          onChange={(e) => onFutureNeedsChange(e.target.value)}
        />
      </div>
      <button
        className="btn btn--primary btn--md"
        type="button"
        disabled={submitting}
        onClick={onSubmit}
      >
        {submitting ? t('feedbackSubmitting') : t('feedbackSubmit')}
      </button>
    </div>
  );
}
